import { PRO014_MIN_ACTIVE_ITEMS, PRO014_RESTRICTION_CHANGE } from "../create/constants";
import type { Pro014EditGateway, Pro014EditPlan, Pro014GradeReplacement } from "../create/types";

const runInSequence = async <T>(
  entries: T[],
  step: (entry: T) => Promise<unknown>,
): Promise<void> => {
  for (const entry of entries) {
    await step(entry);
  }
};

const earlyDeactivationCount = (plan: Pro014EditPlan): number =>
  Math.min(
    plan.itemsToDeactivate.length,
    Math.max(0, plan.activeItemsCount - PRO014_MIN_ACTIVE_ITEMS),
  );

export const runPro014EditPlan = async (
  plan: Pro014EditPlan,
  gateway: Pro014EditGateway,
): Promise<void> => {
  const earlyCount: number = earlyDeactivationCount(plan);
  const reactivated: Set<string> = new Set(plan.itemsToReactivate);
  const isReactivated = (replacement: Pro014GradeReplacement): boolean =>
    reactivated.has(replacement.itemCode);
  const isStillActive = (replacement: Pro014GradeReplacement): boolean =>
    !isReactivated(replacement);

  await runInSequence(plan.itemsToDeactivate.slice(0, earlyCount), gateway.deactivateItem);
  await runInSequence(plan.gradeReplacements.filter(isStillActive), gateway.replaceGrades);
  if (plan.restriction === PRO014_RESTRICTION_CHANGE.ENABLE) {
    await gateway.setGradeRestriction(true);
  }
  if (Object.keys(plan.fields).length > 0) await gateway.updateFields(plan.fields);
  if (plan.restriction === PRO014_RESTRICTION_CHANGE.DISABLE) {
    await gateway.setGradeRestriction(false);
  }
  if (plan.itemsToAdd.length > 0) await gateway.addItems(plan.itemsToAdd, plan.addGradeTargets);
  await runInSequence(plan.gradeReplacements.filter(isReactivated), gateway.replaceGrades);
  await runInSequence(plan.itemsToReactivate, gateway.activateItem);
  await runInSequence(plan.itemsToDeactivate.slice(earlyCount), gateway.deactivateItem);
};
